import {z} from "zod";
import type {ModalSystemProps} from "@/global/providers/ModalProvider";
import {apiRedirectTicket} from "@/modules/tickets/services/tickets.service";
import {type SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Dialog, DialogContent, MenuItem, Stack, Typography} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import {SelectInputForm} from "@/global/components/FormInputs/SelectInputForm";
import {type TicketRedirectDto, ticketRedirectDtoRedirectToEnum} from "@/global/api/gen/ts/TicketRedirectDto";
import {toast} from "react-toastify";

interface Props {
    ticketId: number;
}

enum TicketRedirectPlace {
    'mvd' = 1,
    'developer' = 2
}

const TicketRedirectText: Record<TicketRedirectPlace, string> = {
    [TicketRedirectPlace.mvd]: 'МВД',
    [TicketRedirectPlace.developer]: 'Разработчикам'
};

const zodSchema = z.object({
    redirect_to: z.string().refine((value) => {
        return !!Object.values(ticketRedirectDtoRedirectToEnum).find((el) => el === +value);
    }, {
        message: 'Не выбрано куда перенаправить тикет'
    }),
});

type zodType = z.infer<typeof zodSchema>;

export function TicketSend({open, close, ticketId}: ModalSystemProps<Props>) {

    const {mutate, isPending} = apiRedirectTicket();

    const {control, handleSubmit} = useForm<zodType>({
        resolver: zodResolver(zodSchema)
    });

    const onRedirect: SubmitHandler<zodType> = (data) => {
        const dataWithType = data as unknown as TicketRedirectDto;
        mutate({
            ticket_id: ticketId,
            ...dataWithType
        }, {
            onSuccess: () => {
                toast.success('Тикет успешно перенаправлен');
                close(0);
            }
        });
    };

    return (
        <Dialog
            maxWidth={'sm'}
            fullWidth={true}
            open={open}
            onClose={() => close(5)}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '20px'
                }
            }}
        >
            <DialogContent
                sx={{
                    padding: 4,
                    paddingTop: 8,
                }}
            >
                <form onSubmit={handleSubmit(onRedirect)}>
                    <Stack gap={2}>
                        <Typography
                            color={'#05304F'}
                            textTransform={'uppercase'}
                        >
                            Тикет будет перенаправлен
                        </Typography>
                        <SelectInputForm
                            control={control}
                            name={'redirect_to'}
                            displayEmpty={true}
                            renderValue={(value) => value ? `${TicketRedirectText[value as keyof typeof TicketRedirectText]}` : (
                                <Typography fontWeight={'lighter'}>
                                    Выберите, куда перенаправить
                                </Typography>
                            )}
                            items={Object.entries(TicketRedirectText).map(([key, value]) => (
                                <MenuItem
                                    key={key}
                                    value={key}
                                >
                                    {value}
                                </MenuItem>
                            ))}
                        />
                        <LoadingButton
                            loading={isPending}
                            variant={'contained'}
                            type={'submit'}
                            sx={{
                                marginLeft: 'auto',
                            }}
                        >
                            Перенаправить
                        </LoadingButton>
                    </Stack>
                </form>
            </DialogContent>
        </Dialog>
    );
}